import * as Dialog from '@radix-ui/react-dialog';
import Fuse, { type FuseResult } from 'fuse.js';
import { useEffect, useMemo, useState } from 'react';
import { SquareStack } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useScopeSet } from '@/auth/useScope';
import { usePanelStore } from '@/panels/panelStore';
import { useChatStore } from '@/chat/chatStore';
import { useReducedMotionSafe } from '@/shared/MotionWrappers';
import { useAppStore } from './appStore';
import { navConfig, type NavItem } from './navConfig';

const RECENT_LIMIT = 5;
const RESULTS_LIMIT = 10;
const RECENT_LS_KEY = 'trms_workbench_command_palette_recent_v1';

function readRecent(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === 'string').slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
}

function writeRecent(values: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RECENT_LS_KEY, JSON.stringify(values.slice(0, RECENT_LIMIT)));
  } catch {
    /* quota / privacy mode — ignore */
  }
}

function scrollPanelIntoView(panelId: string): void {
  const el = document.querySelector<HTMLElement>(`[data-panel-id="${panelId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  el.classList.add('ring-2', 'ring-accent');
  setTimeout(() => {
    el.classList.remove('ring-2', 'ring-accent');
  }, 400);
}

type ScoredItem = { item: NavItem; label: string };

function resolveSurfaceLabel(surface: string, tNav: (key: string, opts?: Record<string, unknown>) => string): string {
  for (const section of navConfig) {
    for (const item of section.items) {
      if (item.surface === surface) {
        return tNav(`surfaces.${item.labelKey}`, { defaultValue: item.labelKey });
      }
    }
  }
  return surface;
}

export function CommandPalette() {
  const { t } = useTranslation('common');
  const { t: tNav } = useTranslation('nav');
  const { t: tChat } = useTranslation('chat');
  const open = useAppStore((s) => s.commandPaletteOpen);
  const setOpen = useAppStore((s) => s.setCommandPaletteOpen);
  const scopes = useScopeSet();
  const openSurface = usePanelStore((s) => s.openSurface);
  const panels = usePanelStore((s) => s.panels);
  const appendChat = useChatStore((s) => s.append);
  const reduced = useReducedMotionSafe();
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<string[]>(() => readRecent());

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const allowedItems = useMemo<ScoredItem[]>(() => {
    return navConfig.flatMap((section) =>
      section.items
        .filter((i) => i.scopes.every((sc) => scopes.has(sc)))
        .map((item) => ({
          item,
          label: tNav(`surfaces.${item.labelKey}`, { defaultValue: item.labelKey }),
        })),
    );
  }, [scopes, tNav]);

  const fuse = useMemo(
    () =>
      new Fuse(allowedItems, {
        includeScore: true,
        threshold: 0.4,
        ignoreLocation: true,
        keys: [
          { name: 'label', weight: 0.7 },
          { name: 'item.surface', weight: 0.3 },
        ],
      }),
    [allowedItems],
  );

  const surfaceMatches = useMemo<ScoredItem[]>(() => {
    const q = query.trim();
    if (!q) {
      const allowedBySurface = new Map(allowedItems.map((entry) => [entry.item.surface, entry]));
      const recentEntries = recent
        .map((surface) => allowedBySurface.get(surface))
        .filter((entry): entry is ScoredItem => entry != null);
      const used = new Set(recentEntries.map((entry) => entry.item.surface));
      const padded = allowedItems
        .filter((entry) => !used.has(entry.item.surface))
        .slice(0, RECENT_LIMIT + 3);
      return [...recentEntries, ...padded].slice(0, 8);
    }
    const results: FuseResult<ScoredItem>[] = fuse.search(q, { limit: RESULTS_LIMIT });
    return results.map((r) => r.item);
  }, [allowedItems, fuse, query, recent]);

  const openPanels = useMemo(() => {
    return panels
      .filter((p) => !p.detached)
      .map((p) => ({
        panelId: p.panelId,
        label: resolveSurfaceLabel(p.surface, tNav),
      }));
  }, [panels, tNav]);

  const filteredOpenPanels = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return openPanels;
    return openPanels.filter((p) => p.label.toLowerCase().includes(q));
  }, [openPanels, query]);

  function rememberRecent(surface: string) {
    const next = [surface, ...recent.filter((entry) => entry !== surface)].slice(0, RECENT_LIMIT);
    setRecent(next);
    writeRecent(next);
  }

  function handleAskAgent() {
    const content = query.trim();
    if (!content) return;
    appendChat({
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    });
    setOpen(false);
  }

  function handlePick(item: NavItem) {
    openSurface(item.surface, undefined, {
      title: tNav(`surfaces.${item.labelKey}`, { defaultValue: item.labelKey }),
    });
    rememberRecent(item.surface);
    setOpen(false);
  }

  function handleJumpToPanel(panelId: string) {
    setOpen(false);
    // Scroll after dialog closes
    setTimeout(() => scrollPanelIntoView(panelId), 50);
  }

  const showRecentLabel = !query.trim() && recent.length > 0;
  const contentMotionClass = reduced
    ? ''
    : 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95';

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={`fixed inset-0 z-40 bg-background/80 backdrop-blur-sm ${reduced ? '' : 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'}`}
        />
        <Dialog.Content
          className={`fixed left-1/2 top-[20%] z-50 w-[92%] max-w-xl -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-card shadow-xl ${contentMotionClass}`}
          data-testid="command-palette"
        >
          <Dialog.Title className="sr-only">
            {t('commandPalette', { defaultValue: 'Command palette' })}
          </Dialog.Title>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('commandPalettePlaceholder', {
              defaultValue: 'Ask the agent or jump to a surface…',
            })}
            className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none"
            data-testid="command-palette-input"
          />
          <div className="max-h-80 overflow-y-auto">
            {query.trim() && (
              <div className="border-b border-border p-2">
                <button
                  type="button"
                  onClick={handleAskAgent}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <span>{tChat('askAgent', { defaultValue: 'Ask the agent' })}: "{query}"</span>
                  <kbd className="font-mono text-xs text-muted-foreground">↵</kbd>
                </button>
              </div>
            )}

            {/* Open panels group */}
            {filteredOpenPanels.length > 0 && (
              <div>
                <div className="px-3 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {t('palette.openPanels', { defaultValue: 'Open panels' })}
                </div>
                <ul className="p-2" data-testid="command-palette-open-panels">
                  {filteredOpenPanels.map(({ panelId, label }) => (
                    <li key={panelId}>
                      <button
                        type="button"
                        onClick={() => handleJumpToPanel(panelId)}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                        data-testid={`command-palette-panel-${panelId}`}
                      >
                        <SquareStack size={14} className="text-muted-foreground" />
                        <span>{label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {showRecentLabel && (
              <div className="px-3 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                {t('commandPaletteRecent', { defaultValue: 'Recent' })}
              </div>
            )}
            <ul className="p-2" data-testid="command-palette-results">
              {surfaceMatches.length === 0 ? (
                <li className="px-2 py-1.5 text-xs text-muted-foreground">
                  {t('commandPaletteEmpty', { defaultValue: 'No matching surfaces.' })}
                </li>
              ) : (
                surfaceMatches.map(({ item, label }) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.surface}>
                      <button
                        type="button"
                        onClick={() => handlePick(item)}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                        data-testid={`command-palette-item-${item.surface}`}
                      >
                        <Icon size={14} className="text-muted-foreground" />
                        <span>{label}</span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
